import React, { useMemo } from 'react';
import { Box, Paper, Typography, TextField } from '@mui/material';
import StorageFactory from '../../../Util/StorageFactory';
import { SESSION_USER_COUNTRY_DATA_KEY } from '../../../Util/VMConstants';
import { formatAmount, handleWheel, toTitleCase } from '../../../Util/UtilHelper';
import MUITypography from '../../../Components/MUITypography';

function AdditionalServicesBilling({
  bookedEventState = [],
  updateAdditionalServiceHandler,
}) {
  const sessionFactory = new StorageFactory('session');
  const currencySymbol =
    sessionFactory.get(SESSION_USER_COUNTRY_DATA_KEY)?.currency_symbol || '₹';

  const totalServiceCharges = useMemo(
    () =>
      bookedEventState.reduce((acc, event) => {
        const services = event.service_details || [];
        return (
          acc +
          services.reduce(
            (sum, s) => sum + (parseFloat(s.service_charge) || 0),
            0
          )
        );
      }, 0),
    [bookedEventState]
  );

  const hasAnyService = useMemo(
    () =>
      bookedEventState.some(
        e => Array.isArray(e.service_details) && e.service_details.length > 0
      ),
    [bookedEventState]
  );

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
      <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
        <Typography
          fontWeight={600}
          fontSize={18}
          lineHeight="27px"
          color="#000"
        >
          Additional Services
        </Typography>
      </Box>

      <Paper
        sx={{
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          p: 2,
          borderRadius: '10px',
          backgroundColor: '#FFFFFF',
          boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.09)',
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Typography
              fontWeight={700}
              fontSize={16}
              lineHeight="24px"
              color="#000"
            >
              Total Service Charges
            </Typography>

            <Typography
              fontWeight={700}
              fontSize={16}
              lineHeight="24px"
              color="#00A10B"
            >
              {formatAmount(totalServiceCharges, currencySymbol)}
            </Typography>
          </Box>

          <Box
            sx={{
              width: '100%',
              borderTop: '1px dashed #BDBDBD',
            }}
          />

          {!hasAnyService ? (
            <Typography
              fontWeight={700}
              fontSize={14}
              lineHeight="21px"
              color="#000"
            >
              No Additional Services Added
            </Typography>
          ) : (
            bookedEventState.map((event, eventIdx) => {
              const services = event.service_details || [];
              if (services.length === 0) return null;

              return (
                <Box key={eventIdx} sx={{ minWidth: 0 }}>
                  <Typography
                    fontWeight={700}
                    mb={1.5}
                    sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                  >
                    Event {eventIdx + 1}: {event.event_name}
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      backgroundColor: '#f6f6f6',
                      p: 2,
                      borderRadius: 2,
                      minWidth: 0,
                    }}
                  >
                    {services.map((service, serviceIdx) => (
                      <Box
                        key={`${eventIdx}-${serviceIdx}`}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: {
                            xs: 'minmax(0, 1fr)',
                            sm: 'repeat(2, minmax(0, 1fr))',
                          },
                          gap: 2,
                          minWidth: 0,
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <MUITypography label="Service name" />
                          <TextField
                            fullWidth
                            size="small"
                            value={service.service_name ?? ''}
                            onChange={e =>
                              updateAdditionalServiceHandler?.(
                                eventIdx,
                                serviceIdx,
                                'service_name',
                                e.target.value
                              )
                            }
                            placeholder={toTitleCase('Service name')}
                            sx={{ minWidth: 0 }}
                          />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <MUITypography label="Service charge" />
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            value={service.service_charge ?? ''}
                            onChange={e =>
                              updateAdditionalServiceHandler?.(
                                eventIdx,
                                serviceIdx,
                                'service_charge',
                                e.target.value
                              )
                            }
                            placeholder={toTitleCase('Service charge')}
                            onWheel={handleWheel}
                            onKeyDown={e => {
                              if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                e.preventDefault();
                              }
                            }}
                            inputProps={{ inputMode: 'numeric' }}
                            sx={{
                              minWidth: 0,
                              '& input[type=number]': {
                                MozAppearance: 'textfield',
                              },
                              '& input[type=number]::-webkit-outer-spin-button': {
                                WebkitAppearance: 'none',
                                margin: 0,
                              },
                              '& input[type=number]::-webkit-inner-spin-button': {
                                WebkitAppearance: 'none',
                                margin: 0,
                              },
                            }}
                          />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </Paper>
    </Box>
  );
}

export default AdditionalServicesBilling;
